import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [inlinePanel] - Inline results panel.
 * @property {HTMLElement} [inlineLoading] - Inline loading indicator.
 * @property {HTMLElement} [inlineError] - Inline fallback message.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {number | null} */
  #scrollResetFrame = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.hasAttribute('data-inline')) {
      this.addEventListener('focusin', this.#handleInlineFocus, { signal });
      this.addEventListener('focusout', this.#handleInlineBlur, { signal });
      document.addEventListener('pointerdown', this.#handleInlineOutside, { signal });
      return;
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    if (this.#scrollResetFrame !== null) cancelAnimationFrame(this.#scrollResetFrame);
    this.#scrollResetFrame = null;
    this.search.cancel?.();
    this.resetSearch.cancel?.();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement && !item.closest('[hidden]'));

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    if (this.hasAttribute('data-inline')) {
      if (activeItem?.id) this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
      else this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
    if (!this.hasAttribute('data-inline') || index >= 0) this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /** @param {MouseEvent} event - A header result tab click. */
  selectSearchTab(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement) || !tab.dataset.searchTab) return;
    this.#activateSearchTab(tab.dataset.searchTab);
  }

  /** @param {KeyboardEvent} event - Keyboard navigation within the result tabs. */
  onSearchTabKeyDown(event) {
    if (event.key === 'Escape') {
      this.onSearchKeyDown(event);
      return;
    }
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement)) return;
    const tabs = [...this.querySelectorAll('[data-search-tab]')];
    const index = tabs.indexOf(tab);
    let nextIndex;
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    if (event.key === 'ArrowRight') nextIndex = (index + direction + tabs.length) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - direction + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (nextTab instanceof HTMLButtonElement && nextTab.dataset.searchTab) {
      this.#activateSearchTab(nextTab.dataset.searchTab);
      nextTab.focus();
    }
  }

  /** @param {string} resourceType - The product or collection result group. */
  #activateSearchTab(resourceType) {
    this.#currentIndex = -1;
    for (const tab of this.querySelectorAll('[data-search-tab]')) {
      const selected = tab instanceof HTMLElement && tab.dataset.searchTab === resourceType;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const panel of this.querySelectorAll('[data-search-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.searchPanel !== resourceType;
      if (!panel.hidden) {
        const list = panel.querySelector('[role="listbox"]');
        if (list?.id) this.refs.searchInput.setAttribute('aria-controls', list.id);
        const status = this.querySelector('[data-search-status]');
        if (status) status.textContent = panel.dataset.searchAnnouncement ?? '';
      }
    }
    this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (this.hasAttribute('data-inline')) {
      if (event.target !== this.refs.searchInput && event.key !== 'Escape') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.refs.searchInput.focus();
        this.#closeInline();
        return;
      }
      if (event.key === 'Tab') return;
      if (!this.hasAttribute('data-open')) {
        if (event.key === 'ArrowDown' && this.refs.searchInput.value.trim()) {
          event.preventDefault();
          this.#getInlineSearchResults(this.refs.searchInput.value.trim());
        }
        return;
      }
      if (event.key === 'Enter') {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        return;
      }
    }

    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType && !this.hasAttribute('data-inline')) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    if (this.hasAttribute('data-inline')) {
      if (this.contains(document.activeElement)) this.#getInlineSearchResults(searchTerm);
      return;
    }
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    if (this.#scrollResetFrame !== null) cancelAnimationFrame(this.#scrollResetFrame);
    this.#scrollResetFrame = requestAnimationFrame(() => {
      this.#scrollResetFrame = null;
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /** @param {FocusEvent} event */
  #handleInlineFocus = (event) => {
    if (event.target !== this.refs.searchInput) return;
    const term = this.refs.searchInput.value.trim();
    if (term && !this.hasAttribute('data-open')) this.#getInlineSearchResults(term);
  };

  /** @param {FocusEvent} event */
  #handleInlineBlur = (event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#closeInline();
  };

  /** @param {PointerEvent} event */
  #handleInlineOutside = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closeInline();
  };

  #closeInline() {
    this.#activeFetch?.abort();
    this.removeAttribute('data-open');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.refs.searchInput.removeAttribute('aria-activedescendant');
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    if (this.refs.inlinePanel) this.refs.inlinePanel.hidden = true;
    this.#currentIndex = -1;
  }

  /**
   * Fetch the native result section with cancellation and namespace its IDs so
   * the header and modal can coexist. Early style-owner renders supply its CSS.
   * @param {string} term
   */
  async #getInlineSearchResults(term) {
    const { inlinePanel, inlineLoading, inlineError, predictiveSearchResults, searchInput } = this.refs;
    if (!inlinePanel || !this.dataset.sectionId) return;
    const controller = this.#createAbortController();
    this.setAttribute('data-open', '');
    inlinePanel.hidden = false;
    if (inlineLoading) inlineLoading.hidden = false;
    if (inlineError) inlineError.hidden = true;
    predictiveSearchResults.replaceChildren();
    searchInput.setAttribute('aria-controls', predictiveSearchResults.id);
    predictiveSearchResults.setAttribute('aria-busy', 'true');
    searchInput.setAttribute('aria-expanded', 'true');
    searchInput.removeAttribute('aria-activedescendant');
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[type]', 'product,collection');

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected || searchInput.value.trim() !== term) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('.predictive-search-dropdown');
      if (!results) throw new Error('Missing predictive search results');
      const ids = new Map();
      for (const node of [results, ...results.querySelectorAll('[id]')]) {
        if (!node.id) continue;
        const id = `${this.id}-${node.id}`;
        ids.set(node.id, id);
        node.id = id;
      }
      for (const node of results.querySelectorAll('[aria-controls], [aria-labelledby], [aria-describedby], label[for]')) {
        for (const attribute of ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for']) {
          const value = node.getAttribute(attribute);
          if (value) node.setAttribute(attribute, value.split(' ').map((id) => ids.get(id) ?? id).join(' '));
        }
      }
      results.removeAttribute('role');
      for (const [index, item] of [...results.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')].entries()) {
        item.id ||= `${this.id}-option-${index}`;
        item.setAttribute('role', 'option');
      }
      const wrapper = document.createElement('div');
      wrapper.append(results);
      morph(predictiveSearchResults, wrapper);
      this.#activateSearchTab('products');
      this.#currentIndex = -1;
      this.#resetScrollPositions();
    } catch (error) {
      if (controller.signal.aborted || searchInput.value.trim() !== term) return;
      if (inlineError) inlineError.hidden = false;
    } finally {
      if (this.#activeFetch === controller) {
        predictiveSearchResults.removeAttribute('aria-busy');
        if (inlineLoading) inlineLoading.hidden = true;
      }
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    if (this.hasAttribute('data-inline')) {
      searchInput.value = '';
      predictiveSearchResults.replaceChildren();
      this.#hideResetButton();
      this.#closeInline();
      return;
    }
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
